import { useEffect, useId, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLang, type Key } from '../i18n'
import { api, type Brand, type SlideThemeSummary, type ThemeColorSlot, type TemplateSource } from '../lib/api'
import { reportError } from '../lib/errors'
import { OfficeTemplateList } from './OfficeTemplateList'

/** '#rrggbb' for an <input type="color">; the store keeps the bare 6-hex form. */
const withHash = (v: string): string => `#${v.replace(/^#/, '')}`
const bare = (v: string): string => v.replace(/^#/, '').toUpperCase()

const CORPORATE: Brand = { primary: '1F3864', accent: '2E5FA3', ink: '1F3864' }

/** The palette a PowerPoint template preview shows, in PowerPoint's own order. */
const PREVIEW_SLOTS: readonly ThemeColorSlot[] = [
  'dk1', 'lt1', 'dk2', 'lt2', 'accent1', 'accent2', 'accent3', 'accent4', 'accent5', 'accent6',
]

/**
 * What the server parsed out of a PowerPoint template: the palette, the heading/body fonts and the
 * layout names (display only — localised). A template with several slide masters (light/dark)
 * lets whoever may change it pick the one decks render on. The theme preview of the corporate
 * templates page (CorporateTemplatesPage.tsx).
 */
export function SlidesPreview({
  theme,
  canChoose,
  disabled,
  onChoose,
}: {
  theme: SlideThemeSummary
  canChoose: boolean
  disabled: boolean
  onChoose: (master: number) => void
}) {
  const { t } = useLang()
  const id = useId()
  const master = theme.masters[theme.master] ?? theme.masters[0]
  if (!master) return null
  const swatches = PREVIEW_SLOTS.flatMap((slot) => {
    const hex = master.colors[slot]
    return hex ? [{ slot, hex }] : []
  })
  return (
    <div className="slides-preview">
      {theme.masters.length > 1 &&
        (canChoose ? (
          <label className="brand-field slides-master">
            <span>{t('tpl_slides_master')}</span>
            <select value={theme.master} disabled={disabled} onChange={(e) => onChoose(Number(e.target.value))}>
              {theme.masters.map((m, i) => (
                <option key={i} value={i}>
                  {m.name || `${t('tpl_slides_master')} ${i + 1}`}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p className="brand-hint">
            {t('tpl_slides_master')} {master.name || theme.master + 1}
          </p>
        ))}
      <p id={`${id}-palette`} className="brand-hint">
        {t('tpl_slides_palette')}
      </p>
      <ul className="slides-palette" aria-labelledby={`${id}-palette`}>
        {swatches.map(({ slot, hex }) => (
          <li key={slot} className="slides-swatch" style={{ background: `#${hex}` }} title={`${slot} #${hex}`}>
            <span className="visually-hidden">{`${slot} #${hex}`}</span>
          </li>
        ))}
      </ul>
      <p className="brand-hint">
        {t('tpl_slides_fonts')} {master.fonts.major} / {master.fonts.minor}
      </p>
      <p className="brand-hint">
        {t('tpl_slides_layouts')} {master.layouts.map((l) => l.name || l.type).join(', ')}
      </p>
    </div>
  )
}

const COLORS = [
  ['primary', 'brand_primary'],
  ['accent', 'brand_accent'],
  ['ink', 'brand_ink'],
] as const satisfies ReadonlyArray<readonly [keyof Omit<Brand, 'logoDataUri'>, Key]>

/**
 * The bid's branding on the Bid Settings page: its bid Office templates — two lists, "Bid PowerPoint
 * template" and "Bid Word template" (OfficeTemplateList, over `/v1/bids/:bid/office-templates`,
 * AB#444) — and its brand colours.
 *
 * The colours resolve bid → corporate (the bid's own wins, server-side) as `GET
 * /v1/template-sets?scope=bid` reports them, with their effective source ("Bid", "Corporate", or
 * "None"); the card says when they are derived from the bid's default PowerPoint template. They can
 * be edited and saved, and Reset — offered only when the bid HAS its own colours — drops them so they
 * resolve from the corporate scope again. After any write every `['template-sets']` view is refetched.
 *
 * Everyone on the bid sees the lists and the colours — knowing the branding is not a privilege —
 * but controls only appear when the view says the caller may edit the bid (`canEdit`, the bid's
 * canEditBid). The server refuses such a write regardless, so this is only about not offering a
 * button that would 403.
 */
export function BrandingCard({ bid }: { bid: string }) {
  const { t } = useLang()
  const qc = useQueryClient()
  const ids = useId()

  const { data: view } = useQuery({ queryKey: ['template-sets', 'bid', bid], queryFn: () => api.templateSet('bid', bid) })
  const canEdit = view?.canEdit ?? false

  const [busy, setBusy] = useState(false)

  // The colour draft: the bid's own value, else what it currently inherits.
  const seedBrand = view ? (view.brand.own ?? view.brand.effective) : CORPORATE
  const [brand, setBrand] = useState<Brand>(seedBrand)
  useEffect(() => {
    setBrand({ primary: seedBrand.primary, accent: seedBrand.accent, ink: seedBrand.ink })
  }, [seedBrand.primary, seedBrand.accent, seedBrand.ink])

  /** Run one colours write; the card says when it failed. */
  async function write(op: () => Promise<unknown>): Promise<void> {
    setBusy(true)
    try {
      await op()
      await qc.invalidateQueries({ queryKey: ['template-sets'] })
    } catch {
      reportError(t('brand_save_failed'))
    } finally {
      setBusy(false)
    }
  }

  const sourceLabel = (source: TemplateSource): string => {
    if (source === 'bid') return t('tpl_source_bid')
    if (source === 'corporate') return t('tpl_source_corporate')
    return t('tpl_source_none')
  }

  return (
    <section className="branding-card" aria-label={t('brand_title')}>
      <div className="branding-head">
        <span className="home-eyebrow">{t('brand_title')}</span>
      </div>
      <p className="brand-intro">{t('brand_intro')}</p>

      {!view ? (
        <p aria-live="polite">…</p>
      ) : (
        <div className="brand-comps">
          <OfficeTemplateList bid={bid} kind="slides" canEdit={canEdit} />
          <OfficeTemplateList bid={bid} kind="word" canEdit={canEdit} />

          <div className="brand-comp" role="group" aria-labelledby={`${ids}-brand`} aria-busy={busy || undefined}>
            <div className="brand-comp-head">
              <span id={`${ids}-brand`} className="brand-comp-title">
                {t('tpl_brand')}
              </span>
              <span className="brand-source">{sourceLabel(view.brand.source)}</span>
            </div>
            {view.brand.derived && view.brand.own === null && <p className="brand-hint">{t('tpl_brand_derived')}</p>}
            <div className="brand-colors">
              {COLORS.map(([key, label]) => (
                <label key={key} className="brand-field">
                  <span>{t(label)}</span>
                  <input
                    type="color"
                    disabled={!canEdit}
                    value={withHash(brand[key])}
                    onChange={(e) => {
                      const value = bare(e.target.value)
                      setBrand((b) => ({ ...b, [key]: value }))
                    }}
                  />
                </label>
              ))}
            </div>
            {canEdit && (
              <div className="brand-actions">
                {view.brand.own !== null && (
                  <button
                    type="button"
                    className="mini"
                    onClick={() => void write(() => api.clearTemplateComponent('bid', bid, 'brand'))}
                    disabled={busy}
                  >
                    {t('tpl_reset')}
                  </button>
                )}
                <button
                  type="button"
                  className="mini ok"
                  disabled={busy}
                  onClick={() => void write(() => api.putTemplateBrand('bid', bid, brand))}
                >
                  {t('brand_save')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
